import * as d3 from 'd3';
import { TubeStatus_t } from 'foreman-slink';
import { SentinelBundleTube } from '../../models/bundle';
import { GraphTooltipController } from '../job-details/util/GraphTooltipController';
import { ZoomableChartController } from 'src/utils/helpers/BaseChartController';

const clickedClass = 'bundle-chart-clicked';

const statusToElementName = (status: TubeStatus_t) => {
  switch (status) {
    case TubeStatus_t.Uncleaned:
      return 'circle';
    default:
      return undefined;
  }
};

type BundleChartItemElementName = Exclude<ReturnType<typeof statusToElementName>, undefined>;
type BundleChartItemElement = SVGElementTagNameMap[BundleChartItemElementName];

interface BundleChartItem {
  x: number;
  y: number;
  status: TubeStatus_t;
  element: BundleChartItemElementName;
}

/** Handles drawing a bundle on a D3 scatter plot chart */
export abstract class BundleChartController extends ZoomableChartController {
  protected readonly isRotated: boolean;
  protected readonly tooltip: GraphTooltipController;
  private data: Array<BundleChartItem> = [];
  private circleSize: number = 8;
  private xMin: number = 0;
  private yMin: number = 0;
  private xMax: number = 1;
  private yMax: number = 1;

  private get width() {
    return this.xMax - this.xMin;
  }

  private get height() {
    return this.yMax - this.yMin;
  }

  private get radius() {
    return this.circleSize * 0.5 * 0.75;
  }

  private get stroke() {
    return this.circleSize * 0.5 * 0.25;
  }

  private get viewBox() {
    return {
      xMin: this.xMin - 1,
      yMin: this.yMin - 1,
      width: this.width + 2,
      height: this.height + 2,
      toString: function (this) {
        return `${this.xMin} ${this.yMin} ${this.width} ${this.height}`;
      },
    };
  }

  constructor(svg: SVGSVGElement, isRotated: boolean) {
    super(svg);
    this.isRotated = isRotated;
    this.svg.attr('viewBox', this.viewBox.toString()); // ViewBox allows the SVG to be "responsive"
    this.svg.selectChildren().remove();

    // Initialize circles group
    this.svg.append('g').attr('data-id', 'circles-group-root');

    this.tooltip = new GraphTooltipController(svg);
    this.resetZoom(true);
  }

  public abstract setData(tubes: SentinelBundleTube[]): void;

  protected performZoomReset(
    transform: d3.ZoomBehavior<SVGSVGElement, any>['transform'],
    instant?: boolean | undefined,
  ): void | Promise<void> {
    this.svg.selectAll('.' + clickedClass).classed(clickedClass, false);
    this.selectionInvisible();

    this.svg
      .transition()
      .duration(instant ? 0 : 750)
      .call(transform, new d3.ZoomTransform(1, 0, 0));
  }

  protected performZoom(e: d3.D3ZoomEvent<SVGElement, any>): void | Promise<void> {
    this.svg.select('g').attr('transform', e.transform.toString());
  }

  protected readonly update = (_value: Array<Partial<BundleChartItem>>) => {
    this.data = _value.filter(
      (d): d is BundleChartItem =>
        typeof d.x === 'number' &&
        typeof d.y === 'number' &&
        typeof d.status === 'number' &&
        typeof d.element === 'string',
    );

    this.xMin = d3.min(this.data, d => d.x) ?? 0;
    this.yMin = d3.min(this.data, d => d.y) ?? 0;
    this.xMax = d3.max(this.data, d => d.x) ?? 0;
    this.yMax = d3.max(this.data, d => d.y) ?? 0;

    let minDist: number = Number.MAX_VALUE;
    for (let i = 0; i < this.data.length; i++) {
      const a = this.data[i];
      for (let k = i + 1; k < this.data.length; k++) {
        const b = this.data[k];
        const d = Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
        if (d < minDist) minDist = d;
      }
    }
    this.circleSize = minDist;

    const maxDim = Math.max(this.width, this.height);
    this.zoom.translateExtent([
      [this.xMin - maxDim * 0.5, this.yMin - maxDim * 0.5],
      [this.xMax + maxDim * 0.5, this.yMax + maxDim * 0.5],
    ]);

    this.render();
    this.tooltip.onParentLayoutChanged();
  };

  private readonly render = () => {
    // Inverts the Y axis
    const yScale = d3.scaleLinear().domain([this.yMin, this.yMax]).range([this.yMax, this.yMin]);

    const join = this.svg
      .attr('viewBox', this.viewBox.toString())
      .select('[data-id=circles-group-root]')
      .selectChildren<BundleChartItemElement, BundleChartItem>()
      .data(this.data, d => `${d.x},${d.y},${d.status}`)
      .join(enter => enter.append(d => document.createElementNS('http://www.w3.org/2000/svg', d.element)));

    join
      .filter<SVGCircleElement>(d => d.status === TubeStatus_t.Uncleaned)
      .classed('bundle-chart-circle', () => true)
      .attr('cx', d => d.x)
      .attr('cy', d => yScale(d.y))
      .attr('stroke-width', this.stroke)
      .attr('r', this.radius);

    // To draw additional shapes, first update `statusToElementName` above to
    // include the tube status you would like to draw, and which SVG element it
    // maps to. Then, off of the joined data, filter down to just that tube
    // type, and add that element's attributes like normal. For example:
    // join.filter(d => d.status === TubeStatus_t.Blocked).attr(...).etc(...)
  };

  private readonly selectionInvisible = () => {
    if (this.hasSelection()) return;
    this.tooltip.setVisible(false);
  };

  private readonly hasSelection = (): boolean => {
    return !!this.svg.selectAll('.' + clickedClass).node();
  };
}

/** Handles drawing a square patterned tube sheet on a chart */
export class SquareBundleChartController extends BundleChartController {
  /** Overrides the default setData logic and uses the Sentinel scale to map out square bundles. */
  public setData(data: SentinelBundleTube[]) {
    const mapped = data
      .filter(i => typeof i.row === 'number' && typeof i.column === 'number')
      .map(this.mapTubeToChartData);
    this.update(mapped);
  }

  private readonly mapTubeToChartData = (tube: SentinelBundleTube): BundleChartItem => {
    return {
      x: this.isRotated ? tube.xCoordinate : Math.trunc(tube.xCoordinate / 2),
      y: tube.yCoordinate,
      status: tube.status,
      element: statusToElementName(tube.status) as BundleChartItemElementName,
    };
  };
}

/** Handles drawing a triangle patterned tube sheet on a chart */
export class TriangleBundleChartController extends BundleChartController {
  public setData(data: SentinelBundleTube[]) {
    const mapped = data
      .filter(i => typeof i.row === 'number' && typeof i.column === 'number')
      .map(this.mapTubeToChartData);
    this.update(mapped);
  }

  private readonly mapTubeToChartData = (tube: SentinelBundleTube): BundleChartItem => {
    let x: number;
    let y: number;
    if (this.isRotated) {
      x = 1.732051 * tube.row + 0.866025 * tube.column;
      y = 0.5 * tube.column;
    } else {
      x = tube.row + 0.5 * tube.column;
      y = 0.866025 * tube.column;
    }

    return {
      x,
      y,
      status: tube.status,
      element: statusToElementName(tube.status) as BundleChartItemElementName,
    };
  };
}
